import { differenceInSeconds } from "date-fns";

import { locale as appLocale } from "@/lib/i18n";

const dateLocales: Record<string, string> = {
  fa: "fa-IR-u-ca-persian",
  fr: "fr-FR",
  de: "de-DE",
  es: "es-ES",
  it: "it-IT",
  ar: "ar",
  en: "en-US",
};

function getDateLocale() {
  return dateLocales[appLocale.key] || "en-US";
}

export function getCalendarDateParts(date: Date) {
  const parts = new Intl.DateTimeFormat(getDateLocale(), {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    numberingSystem: "latn",
  }).formatToParts(date);

  const value = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value);

  return {
    year: value("year"),
    month: value("month"),
    day: value("day"),
  };
}

export function format(
  date: string,
  options?: Intl.DateTimeFormatOptions,
  locale = "en-US",
) {
  return new Intl.DateTimeFormat(locale, options).format(new Date(date));
}

export function formatDate(
  date: string,
  options?: Intl.DateTimeFormatOptions,
  locale = getDateLocale(),
) {
  return new Intl.DateTimeFormat(
    locale,
    options || { dateStyle: "short" },
  ).format(new Date(date));
}

export function formatDateToLocalizedParts(
  date: string | Date,
  locale = getDateLocale(),
): string {
  const dateObj = typeof date === "string" ? new Date(date) : date;

  const formatter = new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const parts = formatter.formatToParts(dateObj);
  const year = parts.find((p) => p.type === "year")?.value || "";
  const month = parts.find((p) => p.type === "month")?.value || "";
  const day = parts.find((p) => p.type === "day")?.value || "";

  return `${day}\n${month}\n${year}`;
}

export function formatTime(date: string, locale = getDateLocale()) {
  const options: Intl.DateTimeFormatOptions = {
    hour: "2-digit",
    minute: "2-digit",
  };

  return new Intl.DateTimeFormat(locale, options).format(new Date(date));
}

export function formatTimeWithSeconds(date: string, locale = getDateLocale()) {
  const options: Intl.DateTimeFormatOptions = {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  };

  return new Intl.DateTimeFormat(locale, options).format(new Date(date));
}

export const differencePercentage = (startDate: string, endDate: string) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const now = new Date();

  const totalDuration = differenceInSeconds(end, start);

  if (!isFinite(totalDuration) || totalDuration <= 0) return 0;

  const elapsedDuration = Math.max(differenceInSeconds(now, start), 0);

  return Math.round((elapsedDuration / totalDuration) * 100);
};
